import {
  FileUpload as ExportIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ComponentType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useWidgetContext, useWidgetEvents } from '@ptek/widget-core';
import { WIDGET_EVENTS } from '../../../constants/events';
import { useWidgetAuth } from '../../../context/AuthContext';
import { useRecordingStore } from '../../../stores';
import { AmbientScribeContext } from '../../../types';
import { FEATURE_FLAGS } from '../../../types/featureFlags';
import { isStandaloneMode } from '../../../utils';
import {
  hasFeature,
  isFeatureEnabled,
  useAutoExportConfig,
} from '../../../utils/feature-util';
import {
  getTemplateExportDefaults,
  getTemplateFieldLabels,
  orderFieldKeysByTemplate,
} from '../../../utils/templateUtils';
import {
  getTemplateQuestionnaireQuestions,
  getTemplateQuestionnaireQuestionKeys,
  type TemplateQuestionnaireSection,
  isSingleAnswerQuestion,
} from '../../../utils/templateQuestionnaireUtils';
import {
  createMultiSelectField,
  createDocumentState,
  documentContentUtils,
  hasRenderableDocumentContent,
} from '../utils';

import { MoreHoriz } from '@mui/icons-material';
import docErrorFaceImage from '../../../assets/doc-error-face.svg';
import { TemplateDropdown } from '../../../components';
import { StickyFooter } from '../../../components/LayoutBoxes';
import { AppContext } from '../../../context/AppContext';
import { useUserpilotTracking } from '../../../hooks';
import { useFontScale } from '../../../hooks/useFontScale';
import { DocumentState } from '../../../types/document';
import { TRACK_EVENTS } from '../../../userpilot';
import {
  loadUserSettingsPreferences,
  saveUserSettingsPreferences,
} from '../../../utils/userPreferences';
import { useAIWarningState } from '../hooks/useAIWarningState';
import { useAutoExportDocument } from '../hooks/useAutoExportDocument';
import { useDocumentAutoSave } from '../hooks/useDocumentAutoSave';
import { useDocumentExport } from '../hooks/useDocumentExport';
import { documentStyles as styles } from '../styles';
import { noteFeedbackStyles } from '../styles/noteFeedbackStyles';
import {
  createDocumentField,
  createRichTextField,
  renderSkeletonLines,
} from '../utils';
import { isHtml } from '../utils/macroUtils';
import { ContestEntryCelebration } from '../../Contest/components/ContestEntryCelebration';
import { NoteFeedback } from './NoteFeedback';
import { SpokedSpinner } from './SpokedSpinner';

function getQuestionnaireFieldDefaultValue(
  section: TemplateQuestionnaireSection | undefined
): unknown {
  switch (section?.responseType) {
    case 'select':
      return [];
    case 'boolean':
      return false;
    case 'number':
      return 0;
    case 'date':
      return null;
    case 'text':
    default:
      return '';
  }
}

function withQuestionnaireFieldDefaults(
  content: Record<string, unknown>,
  questionnaireFieldKeys: string[],
  questionnaireSectionsByKey: Record<string, TemplateQuestionnaireSection>
): Record<string, unknown> {
  const contentWithDefaults = { ...content };

  questionnaireFieldKeys.forEach((key) => {
    if (key in contentWithDefaults) {
      return;
    }

    contentWithDefaults[key] = getQuestionnaireFieldDefaultValue(
      questionnaireSectionsByKey[key]
    );
  });

  return contentWithDefaults;
}

export const DocumentPanel: ComponentType = () => {
  const { sessionModel, generateDocument, updateSessionModel } =
    useContext(AppContext);

  const [documentState, setDocumentState] = useState<DocumentState | null>(
    null
  );

  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());

  const { scaleFontSize } = useFontScale();

  const { user } = useWidgetAuth();
  const { emitToHost } = useWidgetEvents();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();

  const { clearActiveRecording, setSavedTranscriptData } = useRecordingStore();
  const showContextTab = hasFeature(FEATURE_FLAGS.DOCUMENT_CONTEXT);
  const noteFeedbackEnabled = hasFeature(FEATURE_FLAGS.SCRIBE_FEEDBACK);
  const autoExportConfig = useAutoExportConfig();
  const { performAutoExport } = useAutoExportDocument();
  const [isReexporting, setIsReexporting] = useState(false);

  const configurableMenuActions =
    widgetContext.context?.configurableMenuActions;
  const chartNoteFont = widgetContext.context?.appointment?.chartNoteFont;

  const primaryAction = configurableMenuActions?.primaryExportAction;
  const secondaryActions =
    configurableMenuActions?.secondaryExportActions || [];

  const userId = user?.username || user?.email;

  const { showWarning, handleDismissWarning } = useAIWarningState(userId);

  const { saveNow, debouncedSave } = useDocumentAutoSave({
    documentState,
  });

  const { trackEvent } = useUserpilotTracking();

  const { formatFieldLabel, getFieldValue } = documentContentUtils;

  const { handleExport } = useDocumentExport({
    saveCurrentEdits: saveNow,
  });

  const currentDocumentIdRef = useRef<string | null>(null);
  // Export-default bookkeeping for the current document render key: whether
  // the init ran with a resolved template, the field keys it used, and whether
  // the user has toggled a checkbox on this note.
  const exportInitHadTemplateRef = useRef(false);
  const exportFieldKeysRef = useRef<string[]>([]);
  const userToggledFieldsRef = useRef(false);
  const [showContestCelebration, setShowContestCelebration] = useState(false);

  const selectedDocument = useMemo(() => {
    if (!sessionModel.selectedTemplateId) return null;

    return (
      sessionModel.documents.find(
        (doc) => doc.templateId === sessionModel.selectedTemplateId
      ) ?? null
    );
  }, [sessionModel.documents, sessionModel.selectedTemplateId]);

  const selectedTemplate = useMemo(() => {
    if (!sessionModel.selectedTemplateId) return null;

    return (
      sessionModel.templates.find(
        (template) => template.id === sessionModel.selectedTemplateId
      ) ?? null
    );
  }, [sessionModel.templates, sessionModel.selectedTemplateId]);

  const fieldLabels = useMemo(
    () => getTemplateFieldLabels(selectedTemplate),
    [selectedTemplate]
  );

  const questionnaireSectionsByKey = useMemo<
    Record<string, TemplateQuestionnaireSection>
  >(
    () =>
      Object.fromEntries(
        getTemplateQuestionnaireQuestions(selectedTemplate).map(
          (section): [string, TemplateQuestionnaireSection] => [
            section.key,
            section,
          ]
        )
      ),
    [selectedTemplate]
  );
  const questionnaireFieldKeys = useMemo<string[]>(
    () => getTemplateQuestionnaireQuestionKeys(selectedTemplate),
    [selectedTemplate]
  );

  const resetSelectedFields = useCallback(() => {
    setSelectedFields(new Set());
  }, []);

  useEffect(() => {
    if (!sessionModel.selectedTemplateId || !sessionModel.documents.length) {
      setDocumentState(null);
      resetSelectedFields();
      currentDocumentIdRef.current = null;
      return;
    }

    if (!selectedDocument) {
      setDocumentState(null);
      resetSelectedFields();
      currentDocumentIdRef.current = null;
      return;
    }

    const documentRenderKey = `${selectedDocument.id}:${chartNoteFont?.fontFamily || ''}:${chartNoteFont?.fontSize || ''}`;

    if (currentDocumentIdRef.current !== documentRenderKey) {
      currentDocumentIdRef.current = documentRenderKey;
      exportInitHadTemplateRef.current = selectedTemplate !== null;
      exportFieldKeysRef.current = [];
      userToggledFieldsRef.current = false;
      const newDocumentState = createDocumentState(
        selectedDocument,
        chartNoteFont
      );
      const documentStateWithQuestionnaireDefaults = newDocumentState
        ? {
            ...newDocumentState,
            content: withQuestionnaireFieldDefaults(
              newDocumentState.content,
              questionnaireFieldKeys,
              questionnaireSectionsByKey
            ),
          }
        : null;
      setDocumentState(documentStateWithQuestionnaireDefaults);

      if (documentStateWithQuestionnaireDefaults?.content) {
        const allFieldKeys = Array.from(
          new Set([
            ...Object.keys(documentStateWithQuestionnaireDefaults.content),
            ...questionnaireFieldKeys,
          ])
        );

        exportFieldKeysRef.current = allFieldKeys;

        let selectedFieldsSet: Set<string>;

        // When the export checkboxes are visible and the selected template
        // declares exportByDefault on any section or question, use the
        // template's per-field defaults instead of the user's saved list.
        // Template defaults win because the user can still toggle before
        // exporting; the saved list stays in localStorage and applies on
        // notes with templates that declare no exportByDefault at all.
        const { hasDefaults, uncheckedKeys } =
          getTemplateExportDefaults(selectedTemplate);

        if (showContextTab && hasDefaults) {
          selectedFieldsSet = new Set(
            allFieldKeys.filter((key) => !uncheckedKeys.has(key))
          );
        } else {
          const preferences = loadUserSettingsPreferences(userId);
          const savedSelectedFields = preferences.selectedDocumentFields;

          if (savedSelectedFields && savedSelectedFields.length > 0) {
            const savedFieldsInCurrentDoc = allFieldKeys.filter((key) =>
              savedSelectedFields.includes(key)
            );
            selectedFieldsSet = new Set(savedFieldsInCurrentDoc);
          } else {
            selectedFieldsSet = new Set(allFieldKeys);
          }
        }

        setSelectedFields(selectedFieldsSet);
      }
    } else if (
      selectedTemplate &&
      !exportInitHadTemplateRef.current &&
      exportFieldKeysRef.current.length > 0
    ) {
      // The document hydrated before the template loaded (reopened session),
      // so the init above ran without template defaults. Apply them now, but
      // never over a selection the user has already changed on this note.
      exportInitHadTemplateRef.current = true;
      const { hasDefaults, uncheckedKeys } =
        getTemplateExportDefaults(selectedTemplate);

      if (showContextTab && hasDefaults && !userToggledFieldsRef.current) {
        setSelectedFields(
          new Set(
            exportFieldKeysRef.current.filter((key) => !uncheckedKeys.has(key))
          )
        );
      }
    }
  }, [
    sessionModel.selectedTemplateId,
    sessionModel.documents,
    selectedDocument,
    selectedTemplate,
    chartNoteFont,
    userId,
    questionnaireFieldKeys,
    questionnaireSectionsByKey,
    showContextTab,
    resetSelectedFields,
  ]);

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      if (!documentState) return;

      const updatedContent = {
        ...documentState.content,
        [key]: value,
      };

      if (!documentState.isModified) {
        trackEvent(TRACK_EVENTS.DOCUMENT_EDITED, {
          documentId: documentState.id,
          templateId: documentState.templateId,
          templateName: documentState.templateName,
        });
      }

      setDocumentState((prev) =>
        prev
          ? {
              ...prev,
              content: updatedContent,
              isModified: true,
            }
          : null
      );

      debouncedSave(updatedContent);
    },
    [debouncedSave, documentState, trackEvent]
  );

  const handleFieldBlur = useCallback(async () => {
    if (documentState?.content) {
      await saveNow();
    }
  }, [documentState?.content, saveNow]);

  const handleFieldSelection = useCallback(
    (fieldKey: string, isSelected: boolean) => {
      if (!documentState?.content) return;

      userToggledFieldsRef.current = true;
      setSelectedFields((prev) => {
        const updated = new Set(prev);

        if (isSelected) {
          updated.add(fieldKey);
        } else {
          updated.delete(fieldKey);
        }

        saveUserSettingsPreferences(userId, {
          selectedDocumentFields: Array.from(updated),
        });

        return updated;
      });
    },
    [documentState, userId]
  );

  const handleRetryGeneration = useCallback(async () => {
    if (sessionModel.generationError) {
      updateSessionModel((prev) => ({
        ...prev,
        generationError: false,
      }));

      await generateDocument({
        templateId: sessionModel.selectedTemplateId,
      });
    }
  }, [
    sessionModel.generationError,
    sessionModel.selectedTemplateId,
    updateSessionModel,
    generateDocument,
  ]);

  /**
   * Handle re-export - either export existing document or generate new one then export.
   * Used when auto-export is enabled but user wants to manually trigger export.
   */
  const handleReexport = useCallback(async () => {
    setIsReexporting(true);

    try {
      let docStateToExport = documentState;

      if (!docStateToExport) {
        const generatedDocument = await generateDocument({
          templateId: sessionModel.selectedTemplateId,
        });

        if (generatedDocument) {
          const generatedDocumentState = createDocumentState(
            generatedDocument,
            chartNoteFont
          );
          docStateToExport = {
            ...generatedDocumentState,
            content: withQuestionnaireFieldDefaults(
              generatedDocumentState.content,
              questionnaireFieldKeys,
              questionnaireSectionsByKey
            ),
          };
        }
      }

      if (docStateToExport) {
        performAutoExport({
          ...docStateToExport,
          content: withQuestionnaireFieldDefaults(
            docStateToExport.content,
            questionnaireFieldKeys,
            questionnaireSectionsByKey
          ),
        });
      }
    } finally {
      setIsReexporting(false);
    }
  }, [
    documentState,
    generateDocument,
    sessionModel.selectedTemplateId,
    performAutoExport,
    chartNoteFont,
    questionnaireFieldKeys,
    questionnaireSectionsByKey,
  ]);

  const renderDocumentContent = useMemo(() => {
    return (content: any) => {
      const contentToRender = documentState?.content || content;
      const fieldKeys = orderFieldKeysByTemplate(
        Array.from(
          new Set([...Object.keys(contentToRender), ...questionnaireFieldKeys])
        ),
        selectedTemplate
      );
      const customTemplatesEnabled = isFeatureEnabled(
        widgetContext.context?.features,
        FEATURE_FLAGS.CUSTOM_TEMPLATES
      );

      return (
        <Box sx={styles.documentContentContainer}>
          {fieldKeys.map((key) => {
            const value = contentToRender[key];
            const isSelected = selectedFields.has(key);
            const fieldLabel = fieldLabels[key] ?? formatFieldLabel(key);
            // Convert to string, keeping strings as-is to avoid JSON quotes
            const stringValue =
              typeof value === 'string'
                ? value
                : value != null
                  ? JSON.stringify(value)
                  : '';
            const questionnaireSection = questionnaireSectionsByKey[key];

            // Render HTML, including macro markup, only when CUSTOM_TEMPLATES is enabled.
            if (customTemplatesEnabled && isHtml(stringValue)) {
              return createRichTextField({
                fieldKey: key,
                htmlValue: stringValue,
                fieldLabel,
                onChange: handleFieldChange,
                onBlur: handleFieldBlur,
                scaleFontSize,
                isSelected,
                onSelectionChange: showContextTab
                  ? handleFieldSelection
                  : undefined,
              });
            }

            if (
              questionnaireSection &&
              questionnaireSection.responseType === 'select'
            ) {
              return createMultiSelectField({
                fieldKey: key,
                fieldValue: value,
                fieldLabel,
                options: questionnaireSection.options,
                singleAnswer: isSingleAnswerQuestion(questionnaireSection),
                minSelections: questionnaireSection.minSelections,
                maxSelections: questionnaireSection.maxSelections,
                onChange: handleFieldChange,
                isSelected,
                onSelectionChange: showContextTab
                  ? handleFieldSelection
                  : undefined,
              });
            }

            const rawFieldValue = getFieldValue(documentState, key, value);
            const textFieldValue =
              typeof rawFieldValue === 'string'
                ? rawFieldValue
                : rawFieldValue == null
                  ? ''
                  : JSON.stringify(rawFieldValue);

            return createDocumentField({
              fieldKey: key,
              fieldValue: textFieldValue,
              fieldLabel,
              onChange: handleFieldChange,
              onBlur: handleFieldBlur,
              scaleFontSize,
              isSelected,
              onSelectionChange: showContextTab
                ? handleFieldSelection
                : undefined,
            });
          })}
        </Box>
      );
    };
  }, [
    documentState,
    getFieldValue,
    formatFieldLabel,
    fieldLabels,
    handleFieldChange,
    handleFieldBlur,
    questionnaireSectionsByKey,
    questionnaireFieldKeys,
    scaleFontSize,
    selectedFields,
    selectedTemplate,
    handleFieldSelection,
    showContextTab,
    widgetContext,
  ]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleContestCelebrationComplete = useCallback(() => {
    setShowContestCelebration(false);

    if (!isStandaloneMode(widgetContext.context)) {
      emitToHost(WIDGET_EVENTS.EMITTER_NAMES.CONFIRM_CLOSE);
    }
  }, [emitToHost, widgetContext.context]);

  const handleExportAction = useCallback(
    async (actionType: string, shouldCloseMenu = false) => {
      if (documentState) {
        if (selectedFields.size === 0) return;

        await saveNow();

        const contentWithQuestionnaireDefaults = withQuestionnaireFieldDefaults(
          documentState.content,
          questionnaireFieldKeys,
          questionnaireSectionsByKey
        );
        const filteredContent = Object.fromEntries(
          Object.entries(contentWithQuestionnaireDefaults).filter(([key]) =>
            selectedFields.has(key)
          )
        );

        const contestEntryResponse = await handleExport(
          {
            ...documentState,
            content: filteredContent,
          },
          actionType
        );

        const shouldShowCelebration =
          contestEntryResponse?.data?.created === true;

        if (shouldShowCelebration) {
          setShowContestCelebration(true);
        } else if (!isStandaloneMode(widgetContext.context)) {
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.CONFIRM_CLOSE);
        }

        clearActiveRecording();
        setSavedTranscriptData(null);
        setDocumentState(null);
        resetSelectedFields();

        if (shouldCloseMenu) {
          handleMenuClose();
        }
      }
    },
    [
      documentState,
      saveNow,
      handleExport,
      selectedFields,
      questionnaireFieldKeys,
      questionnaireSectionsByKey,
      widgetContext.context,
      emitToHost,
      clearActiveRecording,
      setSavedTranscriptData,
      handleMenuClose,
      resetSelectedFields,
    ]
  );

  const buttonText = primaryAction?.label || 'Save To Chart';

  const shouldShowDropdown = secondaryActions.length > 0 && showContextTab;

  const saveButtonIconStyles = styles.saveButtonIcon;

  const renderSaveToChart = () => (
    <StickyFooter>
      <Button
        onClick={() => handleExportAction(primaryAction?.type || '', false)}
        variant='pill'
      >
        {buttonText}
      </Button>
    </StickyFooter>
  );

  const renderReexportButton = () => (
    <StickyFooter>
      <Button
        onClick={handleReexport}
        disabled={isReexporting || sessionModel.isGenerating}
        startIcon={<ExportIcon />}
        variant='pill'
      >
        {isReexporting ? 'Exporting...' : 'Export to EHR'}
      </Button>
    </StickyFooter>
  );

  const renderRetryButton = () => (
    <StickyFooter>
      <Button
        onClick={handleRetryGeneration}
        startIcon={<RetryIcon />}
        variant='pill'
        color='error'
      >
        Retry
      </Button>
    </StickyFooter>
  );

  const renderActionButton = () => (
    <StickyFooter>
      <Button
        onClick={() => handleExportAction(primaryAction?.type || '', false)}
        variant='pill'
        sx={{ width: 'calc(100% - 60px)', marginLeft: '20px' }}
      >
        {buttonText}
      </Button>

      <>
        <IconButton
          onClick={handleMenuOpen}
          sx={saveButtonIconStyles}
          data-testid='document-save-overflow-menu-trigger'
        >
          <MoreHoriz />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          PaperProps={{ sx: styles.saveButtonMenu }}
          sx={{ zIndex: 1000001 }}
          transitionDuration={150}
        >
          {secondaryActions.map((action) => (
            <MenuItem
              key={action.type}
              onClick={() => handleExportAction(action.type, true)}
              sx={styles.saveButtonMenuItem}
            >
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    </StickyFooter>
  );

  const renderBodyContent = useCallback(() => {
    const hasRenderableContent = hasRenderableDocumentContent(documentState);
    const documentStateMatchesSelection =
      Boolean(selectedDocument) && documentState?.id === selectedDocument?.id;
    const isHydratingGeneratedDocument =
      Boolean(selectedDocument) &&
      (!documentStateMatchesSelection || !hasRenderableContent);

    const bgGenEnabled = isFeatureEnabled(
      widgetContext.context?.features,
      FEATURE_FLAGS.SCRIBE_BACKGROUND_GENERATION
    );

    if (bgGenEnabled && sessionModel.generationPhase === 'finalizing') {
      return (
        <Box sx={styles.generatingContainer}>
          <SpokedSpinner size={48} />
          <Typography variant='subtitle1' sx={styles.generatingTitle}>
            Finalizing Transcript…
          </Typography>
          <Typography variant='body2' sx={styles.generatingSubtitle}>
            Wrapping up your transcript, don&apos;t go anywhere yet.
          </Typography>
        </Box>
      );
    }

    if (sessionModel.bgJobPending) {
      return (
        <Box sx={styles.generatingContainer}>
          <SpokedSpinner size={48} />
          <Typography variant='subtitle1' sx={styles.generatingTitle}>
            Generating SOAP Note
          </Typography>
          <Typography variant='body2' sx={styles.generatingSubtitle}>
            Your note is generating, come back anytime to view and save your
            note.
          </Typography>
        </Box>
      );
    }

    if (sessionModel.isGenerating) {
      return (
        <Box sx={styles.skeletonContentContainer}>{renderSkeletonLines()}</Box>
      );
    }

    if (sessionModel.generationError) {
      return (
        <Box sx={styles.generationErrorContainer}>
          <Box
            component='img'
            src={docErrorFaceImage}
            alt='Error'
            sx={styles.generationErrorIcon}
          />
          <Typography variant='body2' sx={styles.generationErrorText}>
            Something went wrong while generating your note. Please try again.
          </Typography>
          <Button
            variant='pill'
            onClick={handleRetryGeneration}
            sx={styles.generationErrorButton}
          >
            Generate Again
          </Button>
        </Box>
      );
    }

    // NEB-100535: Show a dedicated empty-note state when a completed recording has no relevant medical content.
    if (
      selectedDocument &&
      documentStateMatchesSelection &&
      !sessionModel.isGenerating &&
      !sessionModel.generationError &&
      !hasRenderableContent
    ) {
      return (
        <Box sx={styles.generationErrorContainer}>
          <Box
            component='img'
            src={docErrorFaceImage}
            alt='Info'
            sx={styles.generationErrorIcon}
          />
          <Typography variant='body2' sx={styles.generationErrorText}>
            No relevant medical content was detected in this recording.
          </Typography>
          <Button
            variant='pill'
            onClick={handleRetryGeneration}
            sx={styles.generationErrorButton}
          >
            Generate Again
          </Button>
        </Box>
      );
    }

    if (isHydratingGeneratedDocument) {
      return (
        <Box sx={styles.skeletonContentContainer}>{renderSkeletonLines()}</Box>
      );
    }

    if (
      documentState?.content &&
      documentStateMatchesSelection &&
      hasRenderableContent
    ) {
      return renderDocumentContent(documentState.content);
    }

    return (
      <Box sx={styles.emptyStateContainer}>
        <Typography variant='subtitle2' sx={styles.emptyStateTitle}>
          No Document Generated
        </Typography>
        <Typography variant='caption' sx={styles.emptyStateDescription}>
          Complete a recording with a selected template to generate a document.
        </Typography>
      </Box>
    );
  }, [
    sessionModel.isGenerating,
    sessionModel.bgJobPending,
    sessionModel.generationPhase,
    widgetContext.context?.features,
    sessionModel.generationError,
    documentState,
    selectedDocument,
    renderDocumentContent,
    handleRetryGeneration,
  ]);

  const bodyStyles = useMemo(() => {
    return documentState?.content
      ? styles.contentBodyScrollable
      : styles.contentBody;
  }, [documentState?.content]);

  // Feedback shows only while a completed note's content is on screen.
  const feedbackDocumentId =
    noteFeedbackEnabled &&
    sessionModel.sessionId &&
    selectedDocument?.status === 'completed' &&
    documentState?.id === selectedDocument.id &&
    hasRenderableDocumentContent(documentState) &&
    !sessionModel.isGenerating &&
    !sessionModel.bgJobPending &&
    sessionModel.generationPhase !== 'finalizing' &&
    !sessionModel.generationError
      ? selectedDocument.id
      : null;

  const footer = (
    <>
      {sessionModel.generationError &&
        !sessionModel.isGenerating &&
        autoExportConfig?.hideSoapNotesTab &&
        renderRetryButton()}

      {documentState &&
        !sessionModel.isGenerating &&
        !sessionModel.generationError &&
        (!autoExportConfig?.autoExportAfterGeneration ||
          documentState.isModified) &&
        (shouldShowDropdown ? renderActionButton() : renderSaveToChart())}

      {autoExportConfig?.autoExportAfterGeneration &&
        !sessionModel.isGenerating &&
        !sessionModel.generationError &&
        documentState &&
        !documentState.isModified &&
        renderReexportButton()}
    </>
  );

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={bodyStyles}>
        <Collapse in={showWarning} timeout={500}>
          <Alert
            variant='outlined'
            severity='warning'
            onClose={() => handleDismissWarning(false)}
            sx={styles.warningAlert}
          >
            <Box sx={styles.warningContainer}>
              <Box component='div' sx={styles.warningTitle}>
                AI can make mistakes. Please review before saving.
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={false}
                    onChange={(e) => {
                      handleDismissWarning(e.target.checked);
                    }}
                    sx={styles.warningCheckbox}
                  />
                }
                label="Don't show again"
                sx={styles.warningCheckboxLabel}
              />
            </Box>
          </Alert>
        </Collapse>

        {!widgetContext.context?.hideTemplateDropdown && (
          <Box sx={{ my: '8px' }}>
            <TemplateDropdown />
          </Box>
        )}

        {renderBodyContent()}
      </Box>

      {feedbackDocumentId ? (
        <Box sx={noteFeedbackStyles.footerStack}>
          <NoteFeedback
            key={feedbackDocumentId}
            sessionId={sessionModel.sessionId}
            documentId={feedbackDocumentId}
          />
          {footer}
        </Box>
      ) : (
        footer
      )}
      {showContestCelebration ? (
        <ContestEntryCelebration
          onComplete={handleContestCelebrationComplete}
        />
      ) : null}
    </Box>
  );
};
